import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Container from "../../hoc/Container";
import { checkRetryPayment, fetchOtherStoreOrder, retryPayment } from "../api";
import { copyTextToClipboard, formatRubPrice, formatUsdtPrice } from "../helpers";
import { isTerminalOrder, isWaitingForFunds } from "../order-status";
import {
  getRememberedOrderToken,
  rememberOrderToken,
  saveRecentStoreOrder,
} from "../order-storage";
import OtherStoreSupportLinks from "../components/OtherStoreSupportLinks";
import RedeemCodesLink from "../components/RedeemCodesLink";
import PaymentRetryButton from "../../components/common/PaymentRetryButton";

function OtherStoreOrderPage() {
  const { orderId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const paidFlag = searchParams.get("paid");
  const urlToken = searchParams.get("token");
  // sessionStorage может быть недоступен — токен из ссылки держим и в состоянии.
  const [kept, setKept] = useState({ orderId, token: urlToken });
  const accessToken =
    urlToken ||
    (kept.orderId === orderId ? kept.token : null) ||
    getRememberedOrderToken(orderId);

  useEffect(() => {
    if (!urlToken) {
      return;
    }
    setKept({ orderId, token: urlToken });
    rememberOrderToken(orderId, urlToken);
    const next = new URLSearchParams(searchParams);
    next.delete("token");
    setSearchParams(next, { replace: true });
  }, [orderId, urlToken, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    let timerId;

    const load = async () => {
      try {
        const data = await fetchOtherStoreOrder(orderId, { accessToken });
        if (!cancelled) {
          setOrder(data);
          if (data?.storeId && data?.orderId) {
            saveRecentStoreOrder(data.storeId, data.orderId, accessToken);
          }
          return data;
        }
      } catch {
        if (!cancelled) {
          setOrder(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
      return null;
    };

    const poll = async () => {
      const data = await load();
      if (cancelled) {
        return;
      }
      if (!isTerminalOrder(data)) {
        timerId = window.setTimeout(poll, 4000);
      }
    };

    setLoading(true);
    poll();

    return () => {
      cancelled = true;
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [orderId, accessToken, reloadKey]);

  const handleCopy = async (key) => {
    const copied = await copyTextToClipboard(key);
    if (copied) {
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(""), 2000);
    }
  };

  const isPaid = order?.paymentStatus === "SUCCESS";
  const isFulfilled = order?.fulfillmentStatus === "SUCCESS";
  const isCancelled = order?.paymentStatus === "CANCELLED";
  const isFailed =
    order?.paymentStatus === "FAILED" || order?.fulfillmentStatus === "FAILED";
  const waitingFunds = isWaitingForFunds(order);
  const needsAuth = Boolean(order?.requiresAuth);

  return (
    <Container>
      <section className="content-block mb-2 other-store-order-page">
        <nav className="other-store-breadcrumb">
          <Link to="/" className="other-store-breadcrumb-link">
            Главная
          </Link>
          <span aria-hidden="true">/</span>
          <span>Заказ</span>
        </nav>

        {loading && !order ? (
          <p className="other-store-description">Загрузка заказа…</p>
        ) : null}

        {!loading && !order ? (
          <p className="other-store-error">Заказ не найден</p>
        ) : null}

        {order ? (
          <div className="other-store-order-card">
            <h1 className="other-store-title">Заказ {order.orderId}</h1>
            <p className="other-store-description">{order.productName}</p>
            <p className="other-store-description">
              Количество: {order.quantity} ·{" "}
              {order.paymentMethod === "CRYPTO"
                ? formatUsdtPrice(order.amountUsdt)
                : formatRubPrice(order.amountRub)}
            </p>
            <p className="other-store-description">Email: {order.email}</p>

            {paidFlag === "0" || isFailed || isCancelled ? (
              <p className="other-store-error">
                {order.paymentError ||
                  order.fulfillmentError ||
                  (isCancelled
                    ? "Оплата отменена."
                    : "Оплата не прошла. Попробуйте снова.")}
              </p>
            ) : null}

            {!isPaid && !isFailed && !isCancelled ? (
              <p className="other-store-description">
                Ожидаем подтверждение оплаты…
              </p>
            ) : null}

            {!isPaid ? (
              <PaymentRetryButton
                paymentRetry={order.paymentRetry}
                failed={isFailed || isCancelled}
                onCheck={() => checkRetryPayment(order.orderId, accessToken)}
                onCreate={() => retryPayment(order.orderId, accessToken)}
                onChanged={() => setReloadKey((value) => value + 1)}
                support={<OtherStoreSupportLinks support={order.support} />}
                className="mb-5 mt-3 flex flex-col items-start gap-1.5"
              />
            ) : null}

            {waitingFunds ? (
              <div className="other-store-delay-notice">
                <p className="other-store-description">
                  {order.fulfillmentError ||
                    "Ваш код задерживается. Можете написать в техподдержку для уточнения сроков."}
                </p>
                <OtherStoreSupportLinks support={order.support} />
              </div>
            ) : null}

            {isPaid && !isFulfilled && !isFailed && !waitingFunds ? (
              <p className="other-store-description">
                Оплата получена. Получаем код из магазина…
              </p>
            ) : null}

            {needsAuth && isPaid ? (
              <div className="other-store-order-auth">
                <p className="other-store-description">
                  Коды на сайте показываются только по персональной ссылке,
                  которая открывается после оплаты. Откройте письмо «Ваши коды»,
                  отправленное на {order.email}: коды есть прямо в нём.
                </p>
                <p className="other-store-description">
                  Или{" "}
                  <Link to="/account" className="other-store-consent-link">
                    войдите в кабинет
                  </Link>{" "}
                  с почтой заказа — коды появятся здесь.
                </p>
                <p className="other-store-description">
                  Письма нет? Проверьте «Спам» или напишите в техподдержку,
                  указав номер заказа.
                </p>
                <OtherStoreSupportLinks support={order.support} />
              </div>
            ) : null}

            {isFulfilled && !needsAuth && order.keys?.length > 0 ? (
              <div className="other-store-keys">
                <h2 className="other-store-purchase-title">Ваши коды</h2>
                <ul className="other-store-keys-list">
                  {order.keys.map((item) => (
                    <li key={item.id || item.key} className="other-store-key-item">
                      <code>{item.key}</code>
                      <button
                        type="button"
                        className="other-store-key-copy"
                        onClick={() => handleCopy(item.key)}
                      >
                        {copiedKey === item.key ? "Скопировано" : "Скопировать"}
                      </button>
                      {item.productName ? (
                        <span className="other-store-key-meta">{item.productName}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
                <RedeemCodesLink storeId={order.storeId} count={order.keys.length} />
                <p className="other-store-description">
                  Код также будет отправлен на {order.email}.
                </p>
              </div>
            ) : null}

            <Link to="/" className="other-store-buy-btn other-store-order-home-link">
              На главную
            </Link>
          </div>
        ) : null}
      </section>
    </Container>
  );
}

export default OtherStoreOrderPage;
